import { EditOutlined } from "@ant-design/icons";
import {
  Alert,
  Tooltip as AntTooltip,
  Button,
  Card,
  Empty,
  Flex,
  Input,
  Segmented,
  Skeleton,
  Spin,
  Table,
  Typography,
} from "antd";
import {
  BubbleController,
  Chart as ChartJS,
  Tooltip as ChartTooltip,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  LogarithmicScale,
  PointElement,
  Title,
} from "chart.js";
import dayjs from "dayjs";
import { Chart } from "react-chartjs-2";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import useCategoriaSerieV2 from "../../hooks/useCategoriaSerieV2";
import useCategoriasV2 from "../../hooks/useCategoriasV2";
import useProdutoSerieV2 from "../../hooks/useProdutoSerieV2";
import formatarDuracao from "../../utils/formatarDuracao";
import useApiV2 from "../../utils/useApiV2";
import ProductZonesEditorModal from "./ProductZonesEditorModal";
import { paramsDoDetalhe } from "./categoriaDaV2";
import {
  hasProductBreakdown,
  productMatchesSearch,
  selectedProductFromRow,
} from "./collectionSelectionHelpers";

ChartJS.register(
  BubbleController,
  LineController,
  LineElement,
  LinearScale,
  LogarithmicScale,
  PointElement,
  ChartTooltip,
  Legend,
  Title
);

const { Text } = Typography;

const QUADRANT_ORDER = ["CONSOLIDACAO", "POTENCIAL", "ALERTA", "INADERENCIA"];

const QUADRANT_META = {
  CONSOLIDACAO: {
    fillColor: "rgba(82, 196, 26, 0.08)",
    label: "CONSOLIDACAO",
    pointBackground: "rgba(82, 196, 26, 0.45)",
    pointBorder: "rgba(56, 158, 13, 0.95)",
  },
  POTENCIAL: {
    fillColor: "rgba(24, 144, 255, 0.08)",
    label: "POTENCIAL",
    pointBackground: "rgba(24, 144, 255, 0.45)",
    pointBorder: "rgba(9, 109, 217, 0.95)",
  },
  ALERTA: {
    fillColor: "rgba(250, 173, 20, 0.08)",
    label: "ALERTA",
    pointBackground: "rgba(250, 173, 20, 0.45)",
    pointBorder: "rgba(212, 136, 6, 0.95)",
  },
  INADERENCIA: {
    fillColor: "rgba(255, 77, 79, 0.08)",
    label: "INADERENCIA",
    pointBackground: "rgba(255, 77, 79, 0.45)",
    pointBorder: "rgba(207, 19, 34, 0.95)",
  },
};

const FILTER_META = {
  ALL: {
    color: "rgba(89, 89, 89, 0.95)",
    label: "TODOS",
  },
  VENDAS_ESTOQUE: {
    color: "rgba(89, 89, 89, 0.95)",
    label: "VENDAS ESTOQUE",
  },
  SEM_FATURAMENTO: {
    color: "rgba(89, 89, 89, 0.95)",
    label: "SEM FATURAMENTO",
  },
  CONSOLIDACAO: {
    color: QUADRANT_META.CONSOLIDACAO.pointBorder,
    label: QUADRANT_META.CONSOLIDACAO.label,
  },
  POTENCIAL: {
    color: QUADRANT_META.POTENCIAL.pointBorder,
    label: QUADRANT_META.POTENCIAL.label,
  },
  ALERTA: {
    color: QUADRANT_META.ALERTA.pointBorder,
    label: QUADRANT_META.ALERTA.label,
  },
  INADERENCIA: {
    color: QUADRANT_META.INADERENCIA.pointBorder,
    label: QUADRANT_META.INADERENCIA.label,
  },
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", {
    currency: "BRL",
    maximumFractionDigits: 2,
    style: "currency",
  }).format(Number(value || 0));

const getCollectionStockRevenue = (item) => {
  const outsideLinkedPeriod = Number(item?.revenue_outside_linked_period) || 0;
  if (outsideLinkedPeriod > 0) {
    return outsideLinkedPeriod;
  }

  const totalRevenue = Number(item?.total_revenue) || 0;
  const zoneRevenue = Number(item?.revenue) || 0;
  return totalRevenue > 0 && zoneRevenue <= 0 ? totalRevenue : 0;
};

const getProductStockRevenue = (item) => {
  if (item?.stock_revenue !== undefined) {
    return Number(item.stock_revenue) || 0;
  }

  return Math.max(
    (Number(item?.total_revenue) || 0) - (Number(item?.revenue) || 0),
    0
  );
};

// Classifies every product in a collection into a mutually exclusive category.
// Thresholds are derived from the collection's own products so that each
// product is measured against its siblings, not against collection-aggregate
// revenues (which are far larger).
// Priority: SEM_FATURAMENTO > SEM_PERMANENCIA > quadrant > VENDAS_ESTOQUE.
// A product with both zone revenue and stock revenue goes to the quadrant
// because zone performance is the primary analytical signal.
const classifyProductsList = (rawProducts) => {
  const normalized = rawProducts.map((p) => ({
    ...p,
    revenue: Number(p.revenue) || 0,
    total_revenue: Number(p.total_revenue) || 0,
    permanence: Number(p.permanence) || 0,
    stock_revenue: getProductStockRevenue(p),
  }));

  const quadrantEligible = normalized.filter(
    (p) => p.revenue > 0 && p.permanence > 0
  );
  const localRevThreshold = getStrictLogCut(
    quadrantEligible.map((p) => p.revenue)
  );
  const localPermThreshold = getStrictLogCut(
    quadrantEligible.map((p) => p.permanence)
  );

  return normalized.map((p) => {
    let _productCategory;
    if (p.total_revenue <= 0 && p.permanence > 0) {
      _productCategory = "SEM_FATURAMENTO";
    } else if (p.revenue > 0 && p.permanence <= 0) {
      _productCategory = "SEM_PERMANENCIA";
    } else if (p.revenue > 0 && p.permanence > 0) {
      _productCategory = classifyQuadrant(
        p,
        localRevThreshold,
        localPermThreshold
      );
    } else if (p.stock_revenue > 0.009) {
      _productCategory = "VENDAS_ESTOQUE";
    } else {
      _productCategory = null;
    }
    return { ...p, _productCategory };
  });
};

const getQuantile = (values, quantile = 0.5) => {
  if (!Array.isArray(values) || values.length === 0) {
    return 0;
  }

  const sorted = [...values].sort((a, b) => a - b);
  const position = (sorted.length - 1) * quantile;
  const lowerIndex = Math.floor(position);
  const upperIndex = Math.ceil(position);

  if (lowerIndex === upperIndex) {
    return sorted[lowerIndex];
  }

  const weight = position - lowerIndex;
  return sorted[lowerIndex] * (1 - weight) + sorted[upperIndex] * weight;
};

const getStrictLogCut = (values, strictnessFactor = 0.2) => {
  const positiveValues = (values || [])
    .map((value) => Number(value) || 0)
    .filter((value) => value > 0);

  if (positiveValues.length === 0) {
    return 0;
  }

  const logValues = positiveValues.map((value) => Math.log(value));
  const median = getQuantile(logValues, 0.5);
  const p25 = getQuantile(logValues, 0.25);
  const p75 = getQuantile(logValues, 0.75);
  const iqr = Math.max(0, p75 - p25);
  return Math.exp(median + strictnessFactor * iqr);
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const quadrantOverlayPlugin = {
  id: "quadrantOverlay",
  afterDatasetsDraw: (chart, _args, pluginOptions) => {
    const { chartArea, ctx, scales } = chart;
    if (!chartArea || !scales?.x || !scales?.y) {
      return;
    }

    const xCut = Number(pluginOptions?.xCut);
    const yCut = Number(pluginOptions?.yCut);

    if (
      !Number.isFinite(xCut) ||
      !Number.isFinite(yCut) ||
      xCut <= 0 ||
      yCut <= 0
    ) {
      return;
    }

    const xPixel = scales.x.getPixelForValue(xCut);
    const yPixel = scales.y.getPixelForValue(yCut);

    if (!Number.isFinite(xPixel) || !Number.isFinite(yPixel)) {
      return;
    }

    const bottom = chartArea.bottom;
    const left = chartArea.left;
    const right = chartArea.right;
    const top = chartArea.top;
    const x = clamp(xPixel, left, right);
    const y = clamp(yPixel, top, bottom);
    const fills = pluginOptions?.fills || {};
    const labels = pluginOptions?.labels || {};

    ctx.save();

    ctx.fillStyle = fills.topLeft || "rgba(24, 144, 255, 0.08)";
    ctx.fillRect(left, top, Math.max(0, x - left), Math.max(0, y - top));

    ctx.fillStyle = fills.topRight || "rgba(82, 196, 26, 0.08)";
    ctx.fillRect(x, top, Math.max(0, right - x), Math.max(0, y - top));

    ctx.fillStyle = fills.bottomLeft || "rgba(255, 77, 79, 0.08)";
    ctx.fillRect(left, y, Math.max(0, x - left), Math.max(0, bottom - y));

    ctx.fillStyle = fills.bottomRight || "rgba(250, 173, 20, 0.08)";
    ctx.fillRect(x, y, Math.max(0, right - x), Math.max(0, bottom - y));

    ctx.strokeStyle = "rgba(80, 80, 80, 0.55)";
    ctx.lineWidth = 1;
    ctx.setLineDash([6, 5]);

    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(right, y);
    ctx.stroke();

    ctx.fillStyle = "rgba(55, 65, 81, 0.9)";
    ctx.font = "600 11px sans-serif";

    if (labels.topLeft) {
      ctx.fillText(labels.topLeft, left + 8, top + 16);
    }
    if (labels.topRight) {
      ctx.fillText(labels.topRight, x + 8, top + 16);
    }
    if (labels.bottomLeft) {
      ctx.fillText(
        labels.bottomLeft,
        left + 8,
        clamp(y + 18, top + 16, bottom - 4)
      );
    }
    if (labels.bottomRight) {
      ctx.fillText(
        labels.bottomRight,
        x + 8,
        clamp(y + 18, top + 16, bottom - 4)
      );
    }

    ctx.restore();
  },
};

const PAGE_SIZE = 20;
const SEM_FILTRO = {};

const classifyQuadrant = (item, revenueThreshold, permanenceThreshold) => {
  const isHighRevenue = item.revenue >= revenueThreshold;
  const isHighPermanence = item.permanence >= permanenceThreshold;

  if (isHighRevenue && isHighPermanence) {
    return "CONSOLIDACAO";
  }
  if (isHighRevenue && !isHighPermanence) {
    return "POTENCIAL";
  }
  if (!isHighRevenue && !isHighPermanence) {
    return "ALERTA";
  }
  return "INADERENCIA";
};

export default function CollectionsBubbleChart({
  selectedDateRange,
  selectedZones,
  selectedCollections,
  selectedSellers = ["all"],
  modoResolvido = true,
  branchId,
  start,
  end,
  filtros,
  modoOuran = false,
}) {
  const [activeCategoryFilter, setActiveCategoryFilter] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedProductRow, setSelectedProductRow] = useState(null);
  const [linearScaleBounds, setLinearScaleBounds] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productsByKey, setProductsByKey] = useState({});
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [highlightedCollectionKey, setHighlightedCollectionKey] =
    useState(null);
  const [tableSearch, setTableSearch] = useState("");
  const fetchRequestedRef = useRef(new Set());
  const chartRef = useRef(null);
  const queryClient = useQueryClient();
  const { getCategoryProducts } = useApiV2();
  const getCollectionProductBreakdown = useCallback(
    (linha) =>
      getCategoryProducts(
        paramsDoDetalhe({ branchId, start, end, filtros, linha })
      ),
    [getCategoryProducts, branchId, start, end, filtros]
  );

  const { timeseries: productTimeseries, events: productEvents } =
    useCategoriaSerieV2({
      branchId,
      start,
      end,
      filtros,
      linha: selectedCollection,
      enabled: !!selectedCollection && modoResolvido,
    });

  const { timeseries: prodTimeseries } = useProdutoSerieV2({
    branchId,
    start,
    end,
    filtros,
    produto: selectedProductRow?.produto,
    linha: selectedProductRow,
    enabled: !!selectedProductRow && modoResolvido,
  });

  const { trajectoryDataset, eventDataset } = useMemo(() => {
    if (!selectedCollection || !productTimeseries.length) {
      return { trajectoryDataset: null, eventDataset: null };
    }

    const evByDate = {};
    productEvents.forEach((ev) => {
      if (!evByDate[ev.date]) evByDate[ev.date] = [];
      evByDate[ev.date].push(ev);
    });

    let cumRev = 0;
    let cumPerm = 0;
    const trajectoryPoints = [];
    const eventPoints = [];

    productTimeseries.forEach((day) => {
      cumRev += day.revenue;
      cumPerm += day.permanence;
      if (cumRev <= 0 || cumPerm <= 0) return;

      trajectoryPoints.push({ x: cumPerm, y: cumRev, date: day.date });

      if (evByDate[day.date]) {
        // Deduplicate by title — multi-zone events produce one entry per zone
        const seenTitles = new Set();
        evByDate[day.date].forEach((ev) => {
          if (seenTitles.has(ev.title)) return;
          seenTitles.add(ev.title);
          eventPoints.push({
            x: cumPerm,
            y: cumRev,
            r: 8,
            eventTitle: ev.title,
            eventComment: ev.comment,
            eventZone: ev.zone,
            eventDate: day.date,
          });
        });
      }
    });

    const color = "#1890ff";

    return {
      trajectoryDataset: {
        type: "line",
        label: `${selectedCollection.label} — trajetória`,
        data: trajectoryPoints,
        borderColor: color,
        backgroundColor: "rgba(24,144,255,0.12)",
        borderWidth: 2,
        pointRadius: 2,
        pointHoverRadius: 5,
        fill: false,
        tension: 0,
        order: 1,
      },
      eventDataset:
        eventPoints.length > 0
          ? {
              type: "bubble",
              label: "Eventos",
              data: eventPoints,
              backgroundColor: "rgba(250,140,22,0.85)",
              borderColor: "#d46b08",
              borderWidth: 2,
              hitRadius: 14,
              hoverRadius: 4,
              order: 0,
            }
          : null,
    };
  }, [selectedCollection, productTimeseries, productEvents]);

  const { productTrajectoryDataset, productEventDataset } = useMemo(() => {
    if (!selectedProductRow || !prodTimeseries.length) {
      return { productTrajectoryDataset: null, productEventDataset: null };
    }

    let cumRev = 0;
    let cumPerm = 0;
    const trajectoryPoints = [];

    prodTimeseries.forEach((day) => {
      cumRev += day.revenue;
      cumPerm += day.permanence;
      if (cumRev <= 0 || cumPerm <= 0) return;
      trajectoryPoints.push({ x: cumPerm, y: cumRev, date: day.date });
    });

    const color = "#52c41a";

    return {
      productTrajectoryDataset: {
        type: "line",
        label: `${selectedProductRow.label} — trajetória`,
        data: trajectoryPoints,
        borderColor: color,
        backgroundColor: "rgba(82,196,26,0.12)",
        borderWidth: 2,
        pointRadius: 2,
        pointHoverRadius: 5,
        fill: false,
        tension: 0,
        order: 1,
      },
      productEventDataset: null,
    };
  }, [selectedProductRow, prodTimeseries]);

  const productOverlayDataset = useMemo(() => {
    if (!selectedProductRow || !prodTimeseries.length) return null;
    // Use the trajectory endpoint so the dot always lands exactly where the
    // line ends — even when permanence comes from collection-level attribution.
    let cumRev = 0;
    let cumPerm = 0;
    prodTimeseries.forEach((day) => {
      cumRev += day.revenue;
      cumPerm += day.permanence;
    });
    if (cumPerm <= 0 || cumRev <= 0) return null;
    const collEntry = Object.values(productsByKey).find(
      (e) =>
        e.status === "loaded" &&
        String(e.legacy_id) === String(selectedProductRow.legacy_id)
    );
    const prodData = collEntry?.products?.find(
      (p) => String(p.produto) === String(selectedProductRow.produto)
    );
    return {
      type: "bubble",
      label: selectedProductRow.label,
      data: [
        {
          x: cumPerm,
          y: cumRev,
          r: 10,
          label: selectedProductRow.label,
          _isProductOverlay: true,
          days: prodData?.days || 0,
          revenuePerDay: Number(prodData?.revenue_per_day) || 0,
          zones: Array.isArray(prodData?.zones) ? prodData.zones : [],
        },
      ],
      backgroundColor: "rgba(82,196,26,0.75)",
      borderColor: "#389e0d",
      borderWidth: 2,
      order: 0,
    };
  }, [selectedProductRow, prodTimeseries, productsByKey]);

  const {
    data: filteredScatterData,
    error: filteredScatterError,
    isLoading: isFilteredScatterLoading,
    produtosSemCategoria: unclassifiedSkus,
  } = useCategoriasV2({
    branchId,
    start,
    end,
    filtros,
    modoOuran,
    enabled: modoResolvido,
  });
  const { data: baselineScatterData, isLoading: isBaselineScatterLoading } =
    useCategoriasV2({
      branchId,
      start,
      end,
      filtros: SEM_FILTRO,
      modoOuran,
      enabled: modoResolvido,
    });
  const isLoading = isFilteredScatterLoading || isBaselineScatterLoading;
  const error = filteredScatterError;

  const {
    classifiedData,
    stockSalesData,
    noPermanenceData,
    noRevenueData,
    permanenceThreshold,
    revenueThreshold,
    scaleRadius,
    xMin,
    yMin,
  } = useMemo(() => {
    const normalizeData = (rawData) =>
      Array.isArray(rawData)
        ? rawData.map((item) => ({
            ...item,
            days: Number(item?.days) || 0,
            label:
              item?.label ||
              (item?.legacy_id ? `Categoria ${item.legacy_id}` : "Sem nome"),
            legacy_id: item?.legacy_id,
            permanence: Number(item?.permanence) || 0,
            revenue: Number(item?.revenue) || 0,
            total_revenue: Number(item?.total_revenue) || 0,
            revenue_outside_linked_period:
              Number(item?.revenue_outside_linked_period) || 0,
            stock_revenue: getCollectionStockRevenue(item),
            revenue_per_day: Number(item?.revenue_per_day) || 0,
            zones: Array.isArray(item?.zones) ? item.zones : [],
            units_sold:
              item?.units_sold != null ? Number(item.units_sold) : null,
            available_stock:
              item?.available_stock != null
                ? Number(item.available_stock)
                : null,
            stock_ref_date: item?.stock_ref_date ?? null,
          }))
        : [];

    const normalizedFilteredData = normalizeData(filteredScatterData);
    const normalizedBaselineData = normalizeData(baselineScatterData);

    const chartEligibleData = normalizedFilteredData.filter(
      (item) => item.permanence > 0 && item.revenue > 0
    );

    const stockRevenueData = normalizedFilteredData.filter(
      (item) => item.stock_revenue > 0
    );
    const zeroRevenueData = normalizedFilteredData.filter(
      (item) =>
        item.total_revenue <= 0 && item.revenue <= 0 && item.permanence > 0
    );
    const zeroPermanenceData = normalizedFilteredData.filter(
      (item) => item.revenue > 0 && item.permanence <= 0
    );

    const thresholdReferenceData =
      normalizedBaselineData.length > 0
        ? normalizedBaselineData
        : normalizedFilteredData;

    const thresholdEligibleData = thresholdReferenceData.filter(
      (item) => item.permanence > 0 && item.revenue > 0
    );

    const dynamicRevenueThreshold = getStrictLogCut(
      thresholdEligibleData.map((item) => item.revenue)
    );
    const dynamicPermanenceThreshold = getStrictLogCut(
      thresholdEligibleData.map((item) => item.permanence)
    );

    const quadrantData = chartEligibleData
      .map((item) => ({
        ...item,
        category: classifyQuadrant(
          item,
          dynamicRevenueThreshold,
          dynamicPermanenceThreshold
        ),
      }))
      .sort((a, b) => b.revenue - a.revenue || b.permanence - a.permanence);

    const maxRevPerDay = chartEligibleData.reduce(
      (maxValue, item) => Math.max(maxValue, Number(item.revenue_per_day) || 0),
      0
    );

    const minPerm = normalizedFilteredData.reduce((minValue, item) => {
      const value = Number(item.permanence) || 0;
      return value > 0 ? Math.min(minValue, value) : minValue;
    }, Infinity);

    const minRev = chartEligibleData.reduce((minValue, item) => {
      const value = Number(item.revenue) || 0;
      return value > 0 ? Math.min(minValue, value) : minValue;
    }, Infinity);

    const xMinValue = Number.isFinite(minPerm)
      ? Math.max(minPerm * 0.8, 0.1)
      : 1;
    const yMinValue = Number.isFinite(minRev) ? Math.max(minRev * 0.8, 0.1) : 1;

    const minRadius = 4;
    const maxRadius = 32;
    const scaleRadiusFn = (value) => {
      if (maxRevPerDay <= 0) {
        return minRadius;
      }

      const normalized = Math.max(0, Number(value) || 0) / maxRevPerDay;
      return minRadius + Math.pow(normalized, 0.65) * (maxRadius - minRadius);
    };

    return {
      classifiedData: quadrantData,
      stockSalesData: stockRevenueData,
      noPermanenceData: zeroPermanenceData,
      noRevenueData: zeroRevenueData,
      permanenceThreshold: dynamicPermanenceThreshold,
      revenueThreshold: dynamicRevenueThreshold,
      scaleRadius: scaleRadiusFn,
      xMin: xMinValue,
      yMin: yMinValue,
    };
  }, [baselineScatterData, filteredScatterData]);

  // SKU-level quadrant thresholds — recomputed from all loaded product entries
  // so that when a product overlay is active the quadrant lines reflect SKU
  // distributions rather than collection aggregates.
  const { skuPermanenceThreshold, skuRevenueThreshold } = useMemo(() => {
    const allProducts = Object.values(productsByKey)
      .filter((e) => e.status === "loaded")
      .flatMap((e) => e.products);
    const eligible = allProducts.filter(
      (p) => Number(p.permanence) > 0 && Number(p.revenue) > 0
    );
    return {
      skuPermanenceThreshold: getStrictLogCut(
        eligible.map((p) => p.permanence)
      ),
      skuRevenueThreshold: getStrictLogCut(eligible.map((p) => p.revenue)),
    };
  }, [productsByKey]);

  const classifiedRows = useMemo(
    () =>
      [...classifiedData]
        .sort((a, b) => b.revenue - a.revenue || b.permanence - a.permanence)
        .map((item, index) => ({
          ...item,
          key: `classified-${item.legacy_id || item.label}-${index}`,
        })),
    [classifiedData]
  );

  const stockSalesRows = useMemo(
    () =>
      [...stockSalesData]
        .sort(
          (a, b) =>
            b.stock_revenue - a.stock_revenue || b.permanence - a.permanence
        )
        .map((item, index) => ({
          ...item,
          category: "VENDAS_ESTOQUE",
          original_total_revenue: item.total_revenue,
          total_revenue: item.stock_revenue,
          revenue: 0,
          revenue_per_day: 0,
          key: `stock-sales-${item.legacy_id || item.label}-${index}`,
        })),
    [stockSalesData]
  );
  const noRevenueRows = useMemo(
    () =>
      [...noRevenueData]
        .sort((a, b) => b.permanence - a.permanence)
        .map((item, index) => ({
          ...item,
          category: "SEM_FATURAMENTO",
          key: `no-revenue-${item.legacy_id || item.label}-${index}`,
        })),
    [noRevenueData]
  );
  const noPermanenceRows = useMemo(
    () =>
      [...noPermanenceData]
        .sort((a, b) => b.revenue - a.revenue || b.permanence - a.permanence)
        .map((item, index) => ({
          ...item,
          category: "SEM_PERMANENCIA",
          key: `no-permanence-${item.legacy_id || item.label}-${index}`,
        })),
    [noPermanenceData]
  );

  // Full deduped list of all collections regardless of active tab.
  // classifiedRows takes priority for collections that appear in multiple lists.
  const allTodosRows = useMemo(() => {
    const seen = new Set();
    return [
      ...classifiedRows,
      ...stockSalesRows,
      ...noRevenueRows,
      ...noPermanenceRows,
    ].reduce((acc, row) => {
      if (!row.legacy_id) {
        acc.push(row);
        return acc;
      }
      if (seen.has(row.legacy_id)) return acc;
      seen.add(row.legacy_id);
      if (row.original_total_revenue !== undefined) {
        acc.push({ ...row, total_revenue: row.original_total_revenue });
      } else {
        acc.push(row);
      }
      return acc;
    }, []);
  }, [classifiedRows, noPermanenceRows, noRevenueRows, stockSalesRows]);

  // Tab filtering uses local _productCategory (per-collection thresholds).
  const filteredClassifiedRows = useMemo(() => {
    if (activeCategoryFilter === "ALL") return allTodosRows;
    if (activeCategoryFilter === "SEM_FATURAMENTO") return noRevenueRows;

    return allTodosRows.filter((row) => {
      if (!hasProductBreakdown(row)) return false;

      if (activeCategoryFilter === "VENDAS_ESTOQUE") {
        const entry = productsByKey[row.key];
        if (!entry || entry.status !== "loaded") return false;
        return entry.products.some(
          (p) => p._productCategory === "VENDAS_ESTOQUE"
        );
      }

      return row.category === activeCategoryFilter;
    });
  }, [activeCategoryFilter, allTodosRows, noRevenueRows, productsByKey]);

  const categoryFilterOptions = useMemo(
    () => [
      {
        label: (
          <span style={{ color: FILTER_META.ALL.color }}>
            {"\u25CF"} {FILTER_META.ALL.label}
          </span>
        ),
        value: "ALL",
      },
      ...QUADRANT_ORDER.map((category) => ({
        label: (
          <span style={{ color: FILTER_META[category].color }}>
            {"\u25CF"} {FILTER_META[category].label}
          </span>
        ),
        value: category,
      })),
      {
        label: (
          <span style={{ color: FILTER_META.VENDAS_ESTOQUE.color }}>
            {"\u25CF"} {FILTER_META.VENDAS_ESTOQUE.label}
          </span>
        ),
        value: "VENDAS_ESTOQUE",
      },
      {
        label: (
          <span style={{ color: FILTER_META.SEM_FATURAMENTO.color }}>
            {"\u25CF"} {FILTER_META.SEM_FATURAMENTO.label}
          </span>
        ),
        value: "SEM_FATURAMENTO",
      },
    ],
    []
  );

  // Data inputs changed — wipe everything including product cache.
  useEffect(() => {
    setCurrentPage(1);
    setSelectedCollection(null);
    setSelectedProductRow(null);
    setLinearScaleBounds(null);
    setProductsByKey({});
    setExpandedRowKeys([]);
    setHighlightedCollectionKey(null);
    setTableSearch("");
    fetchRequestedRef.current = new Set();
  }, [selectedCollections, selectedDateRange, selectedSellers, selectedZones]);

  // Tab switch only — keep product cache so switching tabs doesn't refetch.
  useEffect(() => {
    setCurrentPage(1);
    setSelectedCollection(null);
    setSelectedProductRow(null);
    setLinearScaleBounds(null);
  }, [activeCategoryFilter]);

  // Always eagerly prefetch products for all collections so filtered tabs
  // populate without additional waits.
  useEffect(() => {
    allTodosRows.forEach((row) => {
      if (!hasProductBreakdown(row) || fetchRequestedRef.current.has(row.key))
        return;
      fetchRequestedRef.current.add(row.key);
      setProductsByKey((prev) => ({
        ...prev,
        [row.key]: { status: "loading", products: [], visibleCount: PAGE_SIZE },
      }));
      getCollectionProductBreakdown(row)
        .then((res) => {
          setProductsByKey((prev) => ({
            ...prev,
            [row.key]: {
              status: "loaded",
              legacy_id: row.legacy_id,
              products: classifyProductsList(res?.produtos || []),
              visibleCount: PAGE_SIZE,
            },
          }));
        })
        .catch(() => {
          setProductsByKey((prev) => ({
            ...prev,
            [row.key]: {
              status: "error",
              legacy_id: row.legacy_id,
              products: [],
              visibleCount: PAGE_SIZE,
            },
          }));
        });
    });
  }, [
    allTodosRows,
    getCollectionProductBreakdown,
    selectedDateRange,
    selectedSellers,
    selectedZones,
  ]);

  const handleVerMais = useCallback((collectionKey) => {
    setProductsByKey((prev) => ({
      ...prev,
      [collectionKey]: {
        ...prev[collectionKey],
        visibleCount: prev[collectionKey].visibleCount + PAGE_SIZE,
      },
    }));
  }, []);

  const handleExpand = useCallback(
    (expanded, record) => {
      if (expanded) {
        setExpandedRowKeys((prev) => [...prev, record.key]);
        if (
          !fetchRequestedRef.current.has(record.key) &&
          hasProductBreakdown(record)
        ) {
          fetchRequestedRef.current.add(record.key);
          setProductsByKey((prev) => ({
            ...prev,
            [record.key]: {
              status: "loading",
              products: [],
              visibleCount: PAGE_SIZE,
            },
          }));
          getCollectionProductBreakdown(record)
            .then((res) => {
              setProductsByKey((prev) => ({
                ...prev,
                [record.key]: {
                  status: "loaded",
                  legacy_id: record.legacy_id,
                  products: classifyProductsList(res?.produtos || []),
                  visibleCount: PAGE_SIZE,
                },
              }));
            })
            .catch(() => {
              setProductsByKey((prev) => ({
                ...prev,
                [record.key]: {
                  status: "error",
                  legacy_id: record.legacy_id,
                  products: [],
                  visibleCount: PAGE_SIZE,
                },
              }));
            });
        }
      } else {
        setExpandedRowKeys((prev) => prev.filter((k) => k !== record.key));
      }
    },
    [
      getCollectionProductBreakdown,
      selectedDateRange,
      selectedSellers,
      selectedZones,
    ]
  );

  const dataSourceWithChildren = useMemo(() => {
    const isFiltered = activeCategoryFilter !== "ALL";

    // Products shown for a collection depend on whether the collection has
    // product-level zone data (Case 1) or only collection-level zone data
    // (Case 2 — no WebJasper integration).
    const applyProductFilter = (products, hasIntegration) => {
      if (activeCategoryFilter === "ALL") {
        return products.filter((p) => p._productCategory !== null);
      }

      if (!hasIntegration) {
        // Case 2: no per-product zone attribution. Show all products that
        // have any presencial revenue; tag them so zone columns show "—".
        return products
          .filter((p) => (Number(p.total_revenue) || 0) > 0)
          .map((p) => ({ ...p, _noIntegration: true }));
      }

      if (activeCategoryFilter === "VENDAS_ESTOQUE") {
        return products
          .filter((p) => p._productCategory === "VENDAS_ESTOQUE")
          .map((p) => ({
            ...p,
            original_total_revenue: p.total_revenue,
            total_revenue: p.stock_revenue,
            revenue: 0,
            revenue_per_day: 0,
          }));
      }

      // Local per-collection thresholds.
      return products.filter(
        (p) => p._productCategory === activeCategoryFilter
      );
    };

    // Busca roda sobre a lista inteira de produtos da categoria, antes da
    // paginacao "Ver mais" - senao produto fora da primeira pagina nunca
    // era encontrado.
    const searchQuery = tableSearch.trim().toLowerCase();

    return filteredClassifiedRows.map((row) => {
      const labelMatches =
        !searchQuery || (row.label || "").toLowerCase().includes(searchQuery);
      if (!hasProductBreakdown(row)) {
        return { ...row, _searchMatch: labelMatches };
      }
      const entry = productsByKey[row.key];
      let children;
      let rowOverride = {};
      let searchMatch = labelMatches;

      if (!entry || entry.status === "loading") {
        children = [{ key: `${row.key}-loading`, _isLoading: true }];
      } else if (entry.status === "error") {
        children = [{ key: `${row.key}-error`, _isError: true }];
      } else {
        const hasIntegration = entry.products.some((p) => p.permanence > 0);
        const categoryProducts = applyProductFilter(
          entry.products,
          hasIntegration
        );
        const filtered = labelMatches
          ? categoryProducts
          : categoryProducts.filter((p) =>
              productMatchesSearch(p, searchQuery)
            );
        searchMatch = labelMatches || filtered.length > 0;

        if (isFiltered) {
          if (hasIntegration && categoryProducts.length > 0) {
            // Case 1: collection row shows sum of its filtered-category products.
            const sum = (field) =>
              categoryProducts.reduce((s, p) => s + (Number(p[field]) || 0), 0);
            rowOverride = {
              total_revenue: sum("total_revenue"),
              revenue: sum("revenue"),
              revenue_per_day: sum("revenue_per_day"),
            };
          }
          // Case 2: rowOverride stays {} — scatter metrics used as-is.
        }

        const visible = filtered.slice(0, entry.visibleCount);
        if (visible.length === 0) {
          children = [{ key: `${row.key}-empty`, _isEmpty: true }];
        } else {
          children = visible.map((p, i) => ({
            ...p,
            key: `${row.key}-product-${i}`,
            _isProduct: true,
            // Produto sem categoria nao tem trajetoria nem bolha.
            _semCategoria: !!row.sem_categoria,
            legacy_id: row.legacy_id,
            category_source: row.category_source,
          }));
          if (entry.visibleCount < filtered.length) {
            children.push({
              key: `${row.key}-verMais`,
              _isVerMais: true,
              remaining: Math.min(
                PAGE_SIZE,
                filtered.length - entry.visibleCount
              ),
              _collectionKey: row.key,
            });
          }
        }
      }
      return { ...row, ...rowOverride, children, _searchMatch: searchMatch };
    });
  }, [
    activeCategoryFilter,
    filteredClassifiedRows,
    productsByKey,
    tableSearch,
  ]);

  const searchFilteredDataSource = useMemo(() => {
    if (!tableSearch.trim()) return dataSourceWithChildren;
    return dataSourceWithChildren.filter((row) => row._searchMatch);
  }, [dataSourceWithChildren, tableSearch]);

  const chartData = useMemo(() => {
    const dimOthers = !!selectedCollection || !!selectedProductRow;
    // When a product is selected, dim ALL collection bubbles — the product
    // overlay dot is the only highlighted element. When a collection is
    // selected, keep that collection's bubble at full opacity.
    const activeCollectionId = selectedProductRow
      ? null
      : selectedCollection?.legacy_id;

    return {
      datasets: [
        {
          backgroundColor: classifiedRows.map((item) => {
            const base = QUADRANT_META[item.category]?.pointBackground;
            if (dimOthers && item.legacy_id !== activeCollectionId) {
              return base.replace(/[\d.]+\)$/, "0.06)");
            }
            return base;
          }),
          borderColor: classifiedRows.map((item) => {
            const base = QUADRANT_META[item.category]?.pointBorder;
            if (dimOthers && item.legacy_id !== activeCollectionId) {
              return base.replace(/[\d.]+\)$/, "0.1)");
            }
            return base;
          }),
          borderWidth: 1,
          data: classifiedRows.map((item) => ({
            category: item.category,
            days: item.days,
            label: item.label,
            legacy_id: item.legacy_id,
            r:
              dimOthers && item.legacy_id !== activeCollectionId
                ? scaleRadius(item.revenue_per_day) * 0.6
                : scaleRadius(item.revenue_per_day),
            revenuePerDay: item.revenue_per_day,
            totalRevenue: item.total_revenue,
            revenueOutsideLinkedPeriod: item.revenue_outside_linked_period,
            unitsSold: item.units_sold,
            availableStock: item.available_stock,
            stockRefDate: item.stock_ref_date,
            x: Number(item.permanence) || 0,
            y: Number(item.revenue) || 0,
            zones: item.zones || [],
          })),
          hitRadius: 12,
          hoverRadius: 6,
          label: "Categorias",
        },
        trajectoryDataset,
        eventDataset,
        productTrajectoryDataset,
        productEventDataset,
        productOverlayDataset,
      ].filter(Boolean),
    };
  }, [
    classifiedRows,
    scaleRadius,
    selectedCollection,
    selectedProductRow,
    trajectoryDataset,
    eventDataset,
    productTrajectoryDataset,
    productEventDataset,
    productOverlayDataset,
  ]);

  const handleChartClick = useCallback(
    (_event, elements) => {
      if (!elements.length) {
        setSelectedCollection(null);
        setSelectedProductRow(null);
        setLinearScaleBounds(null);
        return;
      }
      // Only react to clicks on the main bubble dataset (index 0)
      if (elements[0].datasetIndex !== 0) return;
      const item = classifiedRows[elements[0].index];
      if (!item) return;

      if (selectedCollection?.legacy_id === item.legacy_id) {
        setSelectedCollection(null);
        setSelectedProductRow(null);
        setLinearScaleBounds(null);
        return;
      }

      // Compute linear bounds that keep this bubble at the same pixel fraction
      // as it occupies right now on the log scale, so it doesn't visually jump.
      let bounds = null;
      const chart = chartRef.current;
      if (chart) {
        const xs = chart.scales.x;
        const ys = chart.scales.y;
        const bx = Number(item.permanence);
        const by = Number(item.revenue);
        if (bx > 0 && by > 0 && xs.max > xs.min && ys.max > ys.min) {
          const fx =
            (Math.log(bx) - Math.log(xs.min)) /
            (Math.log(xs.max) - Math.log(xs.min));
          const fy =
            (Math.log(by) - Math.log(ys.min)) /
            (Math.log(ys.max) - Math.log(ys.min));
          if (fx > 0 && fx < 1 && fy > 0 && fy < 1) {
            bounds = { xMax: bx / fx, yMax: by / fy };
          }
        }
      }
      setLinearScaleBounds(bounds);
      setSelectedProductRow(null);
      setSelectedCollection({
        key: item.key,
        legacy_id: item.legacy_id,
        label: item.label,
        zones: item.zones,
        zone_ids: item.zone_ids,
        category_source: item.category_source,
      });
    },
    [classifiedRows, selectedCollection]
  );

  const options = useMemo(() => {
    const isLinear = !!selectedCollection || !!selectedProductRow;
    const scaleType = isLinear ? "linear" : "logarithmic";

    return {
      onClick: handleChartClick,
      onHover: (_event, elements) => {
        _event.native.target.style.cursor = elements.length
          ? "pointer"
          : "default";
      },
      animation: { duration: 250 },
      interaction: {
        intersect: true,
        mode: "nearest",
      },
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        quadrantOverlay: {
          fills: {
            bottomLeft: QUADRANT_META.ALERTA.fillColor,
            bottomRight: QUADRANT_META.INADERENCIA.fillColor,
            topLeft: QUADRANT_META.POTENCIAL.fillColor,
            topRight: QUADRANT_META.CONSOLIDACAO.fillColor,
          },
          labels: {
            bottomLeft: QUADRANT_META.ALERTA.label,
            bottomRight: QUADRANT_META.INADERENCIA.label,
            topLeft: QUADRANT_META.POTENCIAL.label,
            topRight: QUADRANT_META.CONSOLIDACAO.label,
          },
          // Suppress quadrant overlay at SKU level — collection thresholds
          // don't apply at product granularity; leave the chart background clean.
          xCut: selectedProductRow ? 0 : permanenceThreshold,
          yCut: selectedProductRow ? 0 : revenueThreshold,
        },
        tooltip: {
          filter: (item, index, array) => {
            // Suppress all collection bubbles when a product is selected,
            // or suppress non-active collection when a collection is selected.
            if (selectedProductRow && item.datasetIndex === 0) {
              return false;
            }
            if (
              selectedCollection &&
              item.datasetIndex === 0 &&
              item.raw?.legacy_id !== selectedCollection.legacy_id
            ) {
              return false;
            }
            // Only show the first trajectory line item
            if (item.dataset.type === "line") {
              return (
                index === array.findIndex((i) => i.dataset.type === "line")
              );
            }
            return true;
          },
          callbacks: {
            title: (contexts) => {
              const ctx = contexts[0];
              if (!ctx) return "";
              if (ctx.raw?.eventTitle !== undefined) return "";
              if (ctx.dataset.type === "line") {
                const d = ctx.raw?.date;
                return d && dayjs(d).isValid()
                  ? dayjs(d).format("DD-MM-YYYY")
                  : "";
              }
              return ctx.raw?.label || "";
            },
            label: (context) => {
              const raw = context.raw || {};

              // Event dot — just name and date
              if (raw.eventTitle !== undefined) {
                const evDate =
                  raw.eventDate && dayjs(raw.eventDate).isValid()
                    ? dayjs(raw.eventDate).format("DD-MM-YYYY")
                    : raw.eventDate || "";
                return `Evento: ${raw.eventTitle} (${evDate})`;
              }

              // Product overlay dot
              if (raw._isProductOverlay) {
                const zones = Array.isArray(raw.zones) ? raw.zones : [];
                const zonesLabel =
                  zones.length > 0 ? zones.join(", ") : "Sem zonas";
                const revenuePerMin =
                  raw.x && raw.y ? Number(raw.y) / Number(raw.x) : 0;
                return [
                  `Zonas: ${zonesLabel}`,
                  `Fat. em Zona: ${formatCurrency(raw.y)}`,
                  `Atenção: ${formatarDuracao(raw.x)}`,
                  `R$/min: ${formatCurrency(revenuePerMin)}`,
                  `Dias em exposição: ${raw.days || 0}`,
                  `Fat./dia (em zona): ${formatCurrency(raw.revenuePerDay)}`,
                ];
              }

              // Trajectory point
              if (context.dataset.type === "line") {
                return [
                  `Atenção acumulada: ${formatarDuracao(raw.x)}`,
                  `Fat. acumulado: ${formatCurrency(raw.y)}`,
                ];
              }

              // Main bubble
              const zones = Array.isArray(raw.zones) ? raw.zones : [];
              const zonesLabel =
                zones.length > 0 ? zones.join(", ") : "Sem zonas";
              const revenuePerMin =
                raw.x && raw.y ? Number(raw.y) / Number(raw.x) : 0;
              const outsideLinkedPeriod =
                Number(raw.revenueOutsideLinkedPeriod) || 0;
              const totalRevenue = Number(raw.totalRevenue) || 0;
              const lines = [
                `Zonas: ${zonesLabel}`,
                `Fat. Presencial: ${formatCurrency(totalRevenue || raw.y)}`,
                `Fat. em Zona: ${formatCurrency(raw.y)}`,
                `Atenção: ${formatarDuracao(raw.x)}`,
                `R$/min: ${formatCurrency(revenuePerMin)}`,
                `Dias em exposi\u00e7\u00e3o: ${raw.days || 0}`,
                `Fat./dia (em zona): ${formatCurrency(raw.revenuePerDay)}`,
              ];
              if (raw.unitsSold != null) {
                lines.push(
                  `Qtd. vendida: ${Number(raw.unitsSold).toLocaleString("pt-BR")} un.`
                );
              }
              if (raw.availableStock != null) {
                const refLabel =
                  raw.stockRefDate && dayjs(raw.stockRefDate).isValid()
                    ? ` (ref. ${dayjs(raw.stockRefDate).format("DD/MM")})`
                    : "";
                lines.push(
                  `Estoque dispon\u00edvel: ${Number(raw.availableStock).toLocaleString("pt-BR")} un.${refLabel}`
                );
              }
              if (outsideLinkedPeriod > 0) {
                lines.push(
                  `\u26a0 Fora vig\u00eancia: ${formatCurrency(outsideLinkedPeriod)}`
                );
              }
              return lines;
            },
          },
          enabled: true,
        },
      },
      responsive: true,
      scales: {
        x: {
          min: isLinear ? 0 : xMin,
          max: isLinear ? linearScaleBounds?.xMax : undefined,
          title: {
            display: true,
            text: "Atenção (min)",
          },
          type: scaleType,
          ticks: isLinear
            ? {
                callback: (v) =>
                  new Intl.NumberFormat("pt-BR", {
                    notation: "compact",
                  }).format(v),
              }
            : {},
        },
        y: {
          min: isLinear ? 0 : yMin,
          max: isLinear ? linearScaleBounds?.yMax : undefined,
          title: {
            display: true,
            text: "Faturamento (R$)",
          },
          type: scaleType,
          ticks: isLinear
            ? {
                callback: (v) =>
                  new Intl.NumberFormat("pt-BR", {
                    notation: "compact",
                    style: "currency",
                    currency: "BRL",
                  }).format(v),
              }
            : {},
        },
      },
    };
  }, [
    permanenceThreshold,
    revenueThreshold,
    skuPermanenceThreshold,
    skuRevenueThreshold,
    xMin,
    yMin,
    handleChartClick,
    selectedCollection,
    selectedProductRow,
    linearScaleBounds,
  ]);

  const classificationColumns = useMemo(() => {
    const isStockFilter = activeCategoryFilter === "VENDAS_ESTOQUE";
    const isQuadrantFilter = QUADRANT_ORDER.includes(activeCategoryFilter);

    return [
      {
        dataIndex: "label",
        key: "label",
        render: (_, record) => {
          if (record._isLoading) {
            return <Spin size="small" />;
          }
          if (record._isError) {
            return <Text type="danger">Erro ao carregar produtos</Text>;
          }
          if (record._isEmpty) {
            const label =
              FILTER_META[activeCategoryFilter]?.label || activeCategoryFilter;
            return (
              <Text type="secondary">
                {activeCategoryFilter !== "ALL"
                  ? `Nenhum produto classificado como ${label} nesta categoria`
                  : "Sem produtos com atividade no período"}
              </Text>
            );
          }
          if (record._isVerMais) {
            return (
              <Button
                size="small"
                type="link"
                onClick={() => handleVerMais(record._collectionKey)}>
                Ver mais {record.remaining} produtos
              </Button>
            );
          }
          if (record._isProduct) {
            return (
              <span>
                {record.descricao}
                {record.ean && (
                  <Text
                    style={{ color: "#8c8c8c", fontSize: 11, marginLeft: 6 }}>
                    {record.ean}
                  </Text>
                )}
              </span>
            );
          }
          if (record.sem_categoria) {
            return (
              <span>
                {record.label}
                <AntTooltip
                  placement="top"
                  title="Produtos vendidos que ainda não têm categoria. Entram no faturamento total, mas não em zona nem no gráfico. Classifique-os em Categorias por SKU.">
                  <Text style={{ color: "#d48806", marginLeft: 8 }}>⚑</Text>
                </AntTooltip>
              </span>
            );
          }
          const outsideLinkedPeriod =
            Number(record?.revenue_outside_linked_period) || 0;
          if (!outsideLinkedPeriod) return record.label;
          return (
            <span>
              {record.label}
              <AntTooltip
                placement="top"
                title={`Ha ${formatCurrency(
                  outsideLinkedPeriod
                )} de faturamento fora da vigencia em zona no periodo selecionado.`}>
                <Text style={{ color: "#d48806", marginLeft: 8 }}>⚑</Text>
              </AntTooltip>
            </span>
          );
        },
        sorter: (a, b) =>
          String(a.label || "").localeCompare(String(b.label || ""), "pt-BR"),
        title: "Produto",
      },
      {
        align: "right",
        dataIndex: "total_revenue",
        defaultSortOrder: "descend",
        key: "total_revenue",
        render: (value, record) => {
          if (
            record._isLoading ||
            record._isError ||
            record._isEmpty ||
            record._isVerMais
          )
            return null;
          return formatCurrency(
            record._isProduct ? record.total_revenue : value
          );
        },
        sorter: (a, b) =>
          (Number(a.total_revenue) || 0) - (Number(b.total_revenue) || 0),
        title: (
          <AntTooltip
            title={
              isStockFilter
                ? "Faturamento presencial vendido fora de vínculo/exposição em zona no período."
                : "Faturamento total presencial da categoria no período, independente de vínculo a zona."
            }>
            {isStockFilter ? "Venda estoque" : "Fat. Presencial"}
          </AntTooltip>
        ),
        width: 160,
      },
      ...(!isStockFilter
        ? [
            {
              align: "right",
              dataIndex: "revenue",
              key: "revenue",
              render: (value, record) => {
                if (
                  record._isLoading ||
                  record._isError ||
                  record._isEmpty ||
                  record._isVerMais
                )
                  return null;
                if (record._isProduct && record._noIntegration) return "—";
                return formatCurrency(
                  record._isProduct ? record.revenue : value
                );
              },
              sorter: (a, b) =>
                (Number(a.revenue) || 0) - (Number(b.revenue) || 0),
              title: (
                <AntTooltip title="Faturamento atribuído ao período em que a categoria esteve vinculada a uma zona. Base do eixo Y do gráfico.">
                  Fat. em Zona
                </AntTooltip>
              ),
              width: 150,
            },
          ]
        : []),
      {
        align: "right",
        dataIndex: "revenue_per_day",
        key: "revenue_per_day",
        render: (value, record) => {
          if (
            record._isLoading ||
            record._isError ||
            record._isEmpty ||
            record._isVerMais
          )
            return null;
          if (record._isProduct && record._noIntegration) return "—";
          return formatCurrency(
            record._isProduct ? record.revenue_per_day : value
          );
        },
        sorter: (a, b) =>
          (Number(a.revenue_per_day) || 0) - (Number(b.revenue_per_day) || 0),
        title: (
          <AntTooltip title="Faturamento em zona dividido pelos dias de exposição. Representa o rendimento diário enquanto a categoria estava ativa em zona.">
            Fat./dia (em zona)
          </AntTooltip>
        ),
        width: 160,
      },
      {
        align: "right",
        dataIndex: "permanence",
        key: "permanence",
        render: (value, record) => {
          if (
            record._isLoading ||
            record._isError ||
            record._isEmpty ||
            record._isVerMais
          )
            return null;
          if (record._isProduct && record._noIntegration) return "—";
          if (record._isProduct) return formatarDuracao(record.permanence);
          return formatarDuracao(value);
        },
        sorter: (a, b) =>
          (Number(a.permanence) || 0) - (Number(b.permanence) || 0),
        title: "Atenção",
        width: 140,
      },
      {
        align: "right",
        dataIndex: "days",
        key: "days",
        render: (value, record) => {
          if (
            record._isLoading ||
            record._isError ||
            record._isEmpty ||
            record._isVerMais
          )
            return null;
          return record._isProduct ? record.days : value;
        },
        sorter: (a, b) => (Number(a.days) || 0) - (Number(b.days) || 0),
        title: "Dias",
        width: 90,
      },
      {
        dataIndex: "zones",
        key: "zones",
        render: (value, record) => {
          if (
            record._isLoading ||
            record._isError ||
            record._isEmpty ||
            record._isVerMais
          )
            return null;
          if (record._isProduct) {
            if (!record.linked)
              return <Text type="secondary">Sem vínculos</Text>;
            return Array.isArray(record.zones) && record.zones.length > 0
              ? record.zones.join(", ")
              : "-";
          }
          return Array.isArray(value) && value.length > 0
            ? value.join(", ")
            : "-";
        },
        title: "Zonas",
        width: 150,
      },
      {
        dataIndex: "actions",
        key: "actions",
        title: "Ações",
        width: 140,
        render: (_, record) => {
          if (
            record._isProduct ||
            record._isLoading ||
            record._isError ||
            record._isEmpty ||
            record._isVerMais
          )
            return null;
          return (
            <AntTooltip title="Editar vínculos de zona">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() =>
                  setEditingProduct({
                    legacy_id: record.legacy_id,
                    label: record.label,
                    category_source: record.category_source,
                  })
                }
                disabled={!record.legacy_id}>
                Editar zonas
              </Button>
            </AntTooltip>
          );
        },
      },
    ];
  }, [activeCategoryFilter, handleVerMais]);

  return (
    <Card
      align="center"
      style={{
        width: "85%",
      }}
      styles={{
        header: {
          backgroundColor: "var(--ouran-card-header-bg)",
        },
      }}
      title="Análise por Categoria (Faturamento x Atenção)">
      <div style={{ minHeight: "420px" }}>
        {isLoading ? (
          <Skeleton active style={{ height: "100%" }} />
        ) : error ? (
          <Alert
            description="Nao foi possivel carregar os dados das categorias."
            message="Erro ao carregar dados"
            showIcon
            type="error"
          />
        ) : classifiedRows.length === 0 ? (
          <Empty description="Sem dados positivos para o periodo selecionado" />
        ) : (
          <Chart
            ref={chartRef}
            data={chartData}
            options={options}
            plugins={[quadrantOverlayPlugin]}
            type="bubble"
          />
        )}
      </div>

      {!isLoading && !error && unclassifiedSkus?.length > 0 && (
        <Alert
          description={`${unclassifiedSkus.length} produto(s) sem categoria definida no período entram na linha Sem Categoria: ${unclassifiedSkus.slice(0, 5).join(", ")}${unclassifiedSkus.length > 5 ? "…" : ""}.`}
          message="Produtos sem categoria"
          showIcon
          style={{ marginTop: 16 }}
          type="warning"
        />
      )}

      {!isLoading && !error && (
        <Flex gap={12} style={{ marginTop: 24 }} vertical>
          <Text type="secondary">
            {selectedProductRow
              ? `Corte SKU — atenção: ${formatarDuracao(skuPermanenceThreshold)} | faturamento: ${formatCurrency(skuRevenueThreshold)}`
              : `Corte de atenção: ${formatarDuracao(permanenceThreshold)} | Corte de faturamento: ${formatCurrency(revenueThreshold)}`}
          </Text>

          <Segmented
            block
            options={categoryFilterOptions}
            onChange={setActiveCategoryFilter}
            value={activeCategoryFilter}
          />

          <Input.Search
            allowClear
            placeholder="Buscar categoria ou produto..."
            style={{ marginBottom: 8 }}
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
          />
          <Table
            columns={classificationColumns}
            dataSource={searchFilteredDataSource}
            expandable={{
              expandedRowKeys,
              onExpand: handleExpand,
              expandIcon: ({ expanded, onExpand, record }) =>
                record._isLoading || record._isProduct ? null : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpand(record, e);
                    }}
                    style={{
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 18,
                      height: 18,
                      padding: 0,
                      fontSize: 12,
                      lineHeight: 1,
                      border: "1px solid #d9d9d9",
                      borderRadius: 2,
                      background: "white",
                      flexShrink: 0,
                      marginRight: 4,
                    }}>
                    {expanded ? "−" : "+"}
                  </button>
                ),
            }}
            onRow={(record) => ({
              onMouseEnter: () => {
                if (
                  !record._isProduct &&
                  !record._isLoading &&
                  !record._isError &&
                  !record._isEmpty &&
                  !record._isVerMais &&
                  record.legacy_id
                ) {
                  setHighlightedCollectionKey(record.key);
                }
              },
              onMouseLeave: () => setHighlightedCollectionKey(null),
              onClick: () => {
                if (record._semCategoria) return;
                if (record._isProduct) {
                  // Product row click → show product bubble + trajectory
                  const isAlreadySelected =
                    selectedProductRow?.produto === record.produto &&
                    selectedProductRow?.legacy_id === record.legacy_id;
                  if (isAlreadySelected) {
                    setSelectedProductRow(null);
                    setLinearScaleBounds(null);
                  } else {
                    // Compute linear bounds from product position so chart
                    // zooms in on the product the same way bubble click does.
                    const collEntry = Object.values(productsByKey).find(
                      (e) =>
                        e.status === "loaded" &&
                        String(e.legacy_id) === String(record.legacy_id)
                    );
                    const prodData = collEntry?.products?.find(
                      (p) => String(p.produto) === String(record.produto)
                    );
                    const isCurrentlyLinear = !!(
                      selectedCollection || selectedProductRow
                    );
                    let bounds = null;
                    // For products without explicit zone attribution,
                    // permanence in the breakdown is 0 but the trajectory
                    // uses collection-level permanence. Fall back to the
                    // parent collection's permanence for the zoom estimate.
                    const collItem = classifiedRows.find(
                      (r) => r.legacy_id === record.legacy_id
                    );
                    const bxFallback = Number(collItem?.permanence || 0);
                    if (
                      !isCurrentlyLinear &&
                      (prodData?.permanence > 0 || bxFallback > 0) &&
                      prodData?.revenue > 0
                    ) {
                      const chart = chartRef.current;
                      if (chart) {
                        const xs = chart.scales.x;
                        const ys = chart.scales.y;
                        const bx =
                          prodData?.permanence > 0
                            ? Number(prodData.permanence)
                            : bxFallback;
                        const by = Number(prodData.revenue);
                        if (
                          bx > 0 &&
                          by > 0 &&
                          xs.max > xs.min &&
                          ys.max > ys.min
                        ) {
                          const fx =
                            (Math.log(bx) - Math.log(xs.min)) /
                            (Math.log(xs.max) - Math.log(xs.min));
                          const fy =
                            (Math.log(by) - Math.log(ys.min)) /
                            (Math.log(ys.max) - Math.log(ys.min));
                          if (fx > 0 && fx < 1 && fy > 0 && fy < 1) {
                            bounds = { xMax: bx / fx, yMax: by / fy };
                          }
                        }
                      }
                    }
                    setLinearScaleBounds(bounds);
                    setSelectedProductRow(selectedProductFromRow(record));
                  }
                  setSelectedCollection(null);
                  return;
                }
                if (
                  record._isLoading ||
                  record._isError ||
                  record._isEmpty ||
                  record._isVerMais ||
                  !record.legacy_id
                ) {
                  return;
                }
                // Collection row click → same as bubble click
                if (selectedCollection?.legacy_id === record.legacy_id) {
                  setSelectedCollection(null);
                  setLinearScaleBounds(null);
                  return;
                }
                // Compute linear bounds so chart zooms to keep this bubble
                // at the same visual position it occupies on the log scale.
                const collItem = classifiedRows.find(
                  (r) => r.legacy_id === record.legacy_id
                );
                const isCurrentlyLinear = !!(
                  selectedCollection || selectedProductRow
                );
                let bounds = null;
                if (!isCurrentlyLinear && collItem) {
                  const chart = chartRef.current;
                  if (chart) {
                    const xs = chart.scales.x;
                    const ys = chart.scales.y;
                    const bx = Number(collItem.permanence);
                    const by = Number(collItem.revenue);
                    if (
                      bx > 0 &&
                      by > 0 &&
                      xs.max > xs.min &&
                      ys.max > ys.min
                    ) {
                      const fx =
                        (Math.log(bx) - Math.log(xs.min)) /
                        (Math.log(xs.max) - Math.log(xs.min));
                      const fy =
                        (Math.log(by) - Math.log(ys.min)) /
                        (Math.log(ys.max) - Math.log(ys.min));
                      if (fx > 0 && fx < 1 && fy > 0 && fy < 1) {
                        bounds = { xMax: bx / fx, yMax: by / fy };
                      }
                    }
                  }
                }
                setSelectedProductRow(null);
                setLinearScaleBounds(bounds);
                setSelectedCollection({
                  legacy_id: record.legacy_id,
                  label: record.label,
                  zones: record.zones,
                  category_source: record.category_source,
                });
              },
              style: {
                cursor:
                  (record._isProduct && !record._semCategoria) ||
                  (!record._isLoading &&
                    !record._isError &&
                    !record._isEmpty &&
                    !record._isVerMais &&
                    record.legacy_id)
                    ? "pointer"
                    : "default",
              },
            })}
            footer={
              activeCategoryFilter !== "ALL" &&
              allTodosRows.some((r) => {
                if (!hasProductBreakdown(r)) return false;
                const e = productsByKey[r.key];
                return !e || e.status === "loading";
              })
                ? () => (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      <Spin size="small" style={{ marginRight: 6 }} />
                      Carregando categorias...
                    </Text>
                  )
                : undefined
            }
            locale={{
              emptyText:
                activeCategoryFilter !== "ALL" &&
                allTodosRows.every((r) => {
                  if (!hasProductBreakdown(r)) return true;
                  const e = productsByKey[r.key];
                  return e && e.status !== "loading";
                })
                  ? "Nenhuma categoria com produtos neste filtro no periodo selecionado"
                  : activeCategoryFilter === "SEM_FATURAMENTO"
                    ? "Nenhuma categoria sem faturamento e com atencao no periodo selecionado"
                    : "Carregando...",
            }}
            pagination={{
              current: currentPage,
              hideOnSinglePage: true,
              onChange: (page) => setCurrentPage(page),
              pageSize: 15,
              showSizeChanger: false,
            }}
            scroll={{ x: 1000 }}
            size="small"
          />
        </Flex>
      )}

      <ProductZonesEditorModal
        open={!!editingProduct}
        product={editingProduct}
        onClose={() => setEditingProduct(null)}
        onChanged={() => {
          queryClient.invalidateQueries({
            queryKey: ["collectionsScatterData"],
          });
          queryClient.invalidateQueries({
            queryKey: ["collectionTimeseries"],
          });
        }}
      />
    </Card>
  );
}
